// Student ID card — printed at real card size (CR80, 54 × 85.6 mm, portrait),
// nine to an A4 sheet. Sized in mm/pt so the printout matches a standard
// card holder; the on-screen preview uses the same component.
import PrintSheet from '../ui/PrintSheet';
import { fmtDate, initials } from '../ui/format';

const BRAND = '#0e6e64';
const INK = '#101b22';
const SOFT = '#5b6b70';

export function IdCard({ student: s, school, validTill }) {
  const phone = s.guardian_phone;
  const address = s.address || s.pickup_address;
  const rows = [
    ['Adm. No.', s.admission_no],
    ['D.O.B.', s.date_of_birth ? fmtDate(s.date_of_birth) : null],
    ['Blood Gr.', s.blood_group],
    s.father_name ? ['Father', s.father_name] : ['Guardian', s.guardian_name],
    ['Phone', phone],
    ['Address', address],
  ].filter(([, v]) => v);

  return (
    <div className="id-card" style={{
      width: '54mm', height: '85.6mm', boxSizing: 'border-box', border: '0.3mm solid #c9d3d0',
      borderRadius: '3mm', overflow: 'hidden', background: '#fff', color: INK,
      display: 'flex', flexDirection: 'column', fontFamily: "'Public Sans', system-ui, sans-serif",
      breakInside: 'avoid', pageBreakInside: 'avoid',
    }}>
      <div style={{ background: BRAND, color: '#fff', padding: '2.2mm 2.5mm 2mm', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Archivo', system-ui, sans-serif", fontWeight: 800, fontSize: '8.5pt', lineHeight: 1.15 }}>
          {school?.name}
        </div>
        {school?.location && <div style={{ fontSize: '5.5pt', opacity: 0.9, marginTop: '0.6mm' }}>{school.location}</div>}
      </div>
      <div style={{ background: '#e3f1ee', color: BRAND, fontSize: '5.5pt', fontWeight: 800, letterSpacing: '0.12em',
        textAlign: 'center', padding: '0.8mm 0' }}>
        STUDENT IDENTITY CARD
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.2mm' }}>
        {s.photo ? (
          <img src={s.photo} alt="" style={{ width: '20mm', height: '24mm', objectFit: 'cover', borderRadius: '1.5mm',
            border: `0.4mm solid ${BRAND}` }} />
        ) : (
          <div style={{ width: '20mm', height: '24mm', borderRadius: '1.5mm', border: `0.4mm dashed ${SOFT}`,
            display: 'grid', placeItems: 'center', fontSize: '14pt', fontWeight: 800, color: SOFT }}>
            {initials(s.name)}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '1.6mm 2mm 0' }}>
        <div style={{ fontFamily: "'Archivo', system-ui, sans-serif", fontWeight: 800, fontSize: '9pt', lineHeight: 1.15 }}>{s.name}</div>
        <div style={{ fontSize: '6.5pt', fontWeight: 700, color: BRAND, marginTop: '0.5mm' }}>
          Class {s.class_name || '—'}{s.roll_number ? ` · Roll ${s.roll_number}` : ''}
        </div>
      </div>

      <div style={{ margin: '1.4mm 2.5mm 0', fontSize: '5.6pt', lineHeight: 1.3,
        display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '1.2mm', rowGap: '0.3mm' }}>
        {rows.map(([label, value]) => [
          <span key={`${label}-l`} style={{ color: SOFT, fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>,
          <span key={`${label}-v`} style={{ fontWeight: 600, display: '-webkit-box', WebkitLineClamp: label === 'Address' ? 2 : 1,
            WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{value}</span>,
        ])}
      </div>

      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        padding: '0 2.5mm 1.8mm', fontSize: '5pt', color: SOFT }}>
        <span>{validTill ? <>Valid till<br /><b style={{ color: INK }}>{fmtDate(validTill)}</b></> : null}</span>
        <span style={{ borderTop: `0.2mm solid ${INK}`, paddingTop: '0.4mm', minWidth: '15mm', textAlign: 'center' }}>Principal</span>
      </div>
      <div style={{ background: BRAND, height: '1.6mm' }} />
    </div>
  );
}

// Print sheet holding one or many cards in a 3-across grid.
export function IdCardSheet({ students, school, validTill, onClose }) {
  return (
    <PrintSheet title={`ID cards (${students.length})`} onClose={onClose}>
      <div className="id-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 54mm)', gap: '5mm', justifyContent: 'center' }}>
        {students.map(s => <IdCard key={s.id} student={s} school={school} validTill={validTill} />)}
      </div>
    </PrintSheet>
  );
}
